import { useMemo } from 'react';

import queryString from 'query-string';

import { MDXAST, TOCItem } from '../../types';
import { getUnifiedCodeLanguageId } from '../../utils/getUnifiedCodeLanguageId';

export const useTOCItems = (
  activeItems: Record<string, string>,
  mdxAST: MDXAST,
): TOCItem[] => {
  const items: TOCItem[] = useMemo(() => {
    const { children } = mdxAST;
    if (children) {
      return children.reduce(
        (
          arr: TOCItem[],
          { type, depth, children: currentChildren },
          i: number,
        ) => {
          // we only display headings of depth 2 or 3
          if (type === 'heading' && (depth === 2 || depth === 3)) {
            const contentIfBefore = children
              // get all the elements before the heading
              .slice(0, i)
              // filter all the elements that are not conditional content
              .filter(({ value }) => value && value.includes('ContentIf'))
              // reverse the order for easier traversal
              .reverse();

            // count the closing tags for ContentIf so that we can determine
            // the parent relationships of the heading
            let closing = 0;
            // the individual queries that the current heading has to fullfil
            // in order to be active
            const queries: string[] = contentIfBefore.reduce(
              (acc: string[], { value }) => {
                // we don't need to add the next opening tag to the queries
                // if there is a corresponding closing tag
                if (value?.includes('</ContentIf')) {
                  closing += 1;
                } else if (closing !== 0) {
                  closing -= 1;
                } else if (closing === 0 && value) {
                  const match = value.match(/query="(.*?)"/);
                  if (match && match[1]) {
                    return [...acc, match[1]];
                  }
                  return acc;
                }
                return acc;
              },
              [],
            );

            // check if all queries are fullfilled
            // else we would display a hidden heading in the TOC
            const active = queries.every(query => {
              const params = queryString.parse(query.toLocaleLowerCase());
              return Object.entries(params).every(
                ([key, value]) =>
                  typeof value === 'string' &&
                  getUnifiedCodeLanguageId(activeItems[key]) ===
                    getUnifiedCodeLanguageId(value),
              );
            });

            if (active) {
              const value = currentChildren?.find(
                ({ type: childType }) => childType === 'text',
              )?.value;

              return [
                ...arr,
                {
                  value: value || '',
                  depth,
                },
              ];
            }
            return arr;
          }
          return arr;
        },
        [],
      );
    }

    return [];
  }, [mdxAST, activeItems]);

  return items;
};
